/**
 * <ugc-video> — a muted, looping UGC clip that plays while it is on screen.
 *
 * Playback is driven from JS rather than the `autoplay` attribute so that only
 * the visible slides of a carousel ever load and play. Sound is opt-in and only
 * one clip on the page can be unmuted at a time.
 */
class UgcVideo extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    this.soundButton = this.querySelector('.ugc-card__sound');
    this.playButton = this.querySelector('.ugc-card__play');
    this.soundIcons = this.querySelectorAll('.ugc-card__sound-icon');
    this.pausedByUser = false;

    this.video.muted = true;
    this.video.loop = true;
    this.video.playsInline = true;
    this.video.setAttribute('playsinline', '');

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplay = this.dataset.autoplay === 'true' && !reducedMotion;

    this.onMediaClick = this.onMediaClick.bind(this);
    this.addEventListener('click', this.onMediaClick);

    this.soundButton?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.toggleSound();
    });

    this.video.addEventListener('play', () => this.syncPlayButton());
    this.video.addEventListener('pause', () => this.syncPlayButton());

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (this.autoplay && !this.pausedByUser) this.play();
          } else {
            this.video.pause();
            this.mute();
          }
        });
      },
      { threshold: 0.35 }
    );

    this.observer.observe(this);
    this.syncPlayButton();
    this.syncSoundIcons();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.removeEventListener('click', this.onMediaClick);
  }

  onMediaClick(event) {
    if (event.target.closest('.ugc-card__sound')) return;

    if (this.video.paused) {
      this.pausedByUser = false;
      this.play();
    } else {
      this.pausedByUser = true;
      this.video.pause();
    }
  }

  play() {
    const attempt = this.video.play();
    // Browsers reject play() when a gesture is required — leave the poster up.
    if (attempt?.catch) attempt.catch(() => this.syncPlayButton());
  }

  toggleSound() {
    if (this.video.muted) {
      document.querySelectorAll('ugc-video').forEach((other) => {
        if (other !== this) other.mute();
      });
      this.video.muted = false;
      // Unmuting counts as intent to watch this clip.
      this.pausedByUser = false;
      if (this.video.paused) this.play();
    } else {
      this.video.muted = true;
    }

    this.syncSoundIcons();
  }

  mute() {
    if (!this.video || this.video.muted) return;
    this.video.muted = true;
    this.syncSoundIcons();
  }

  syncSoundIcons() {
    if (!this.soundButton) return;

    const isMuted = this.video.muted;
    this.soundIcons.forEach((icon, index) => {
      icon.toggleAttribute('hidden', index === 0 ? !isMuted : isMuted);
    });
    this.soundButton.setAttribute('aria-label', isMuted ? 'Unmute video' : 'Mute video');
  }

  syncPlayButton() {
    if (this.playButton) this.playButton.hidden = !this.video.paused;
  }
}

if (!customElements.get('ugc-video')) {
  customElements.define('ugc-video', UgcVideo);
}
